import { useState } from "react";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { claimsFieldWidth } from "../Helpers/claimsFieldWidth";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";

interface CurrencyInputProps {
  dataTestId: string;
  label: string;
  /** Named in the label, so the amount can't be read in the wrong currency. */
  currency: Currency;
  value?: number | undefined;
  onChange: (value: number | undefined) => void;
  errorMessage: ErrorMessageType;
  forceValidation: boolean;
  placeholder?: string | undefined;
}

// Up to nine whole-dollar digits, then at most two decimals. "0." and "." are
// allowed so a customer can type their way to "0.72".
const AMOUNT_PATTERN = /^\d{0,9}(\.\d{0,2})?$/;

// Commas only as thousands separators. "1,50" is a decimal comma, and stripping
// it would read as 150.
const THOUSANDS_PATTERN = /^\d{1,3}(,\d{3})+(\.\d*)?$/;

function parseAmount(text: string): number | undefined {
  const amount = parseFloat(text);
  return Number.isFinite(amount) ? amount : undefined;
}

/**
 * Turns what the customer typed or pasted into amount text, or `undefined` to
 * refuse the edit. A pasted price with more decimals (a unit price like
 * "$0.0725" off an invoice) is rounded to cents; a typed third decimal is
 * refused instead, so the field never changes a digit under the cursor.
 */
function normalizeAmountInput(
  raw: string,
  pasted: boolean,
): string | undefined {
  let input = raw.replace(/[$\s]/g, "");
  if (input.includes(",")) {
    if (!THOUSANDS_PATTERN.test(input)) {
      return undefined;
    }
    input = input.replace(/,/g, "");
  }
  if (pasted && /^\d+\.\d{3,}$/.test(input)) {
    input = parseFloat(input).toFixed(2);
  }
  return AMOUNT_PATTERN.test(input) ? input : undefined;
}

/**
 * A dollars-and-cents amount field, up to two decimal places.
 *
 * Keeps the customer's own text rather than re-printing the number, so a
 * half-typed "0." survives the keystroke. A blank field emits `undefined`, so
 * validation can tell "not entered" from a real 0. Not built on
 * `DimensionInput`, which rounds up to whole units.
 */
export function ClaimsCurrencyInput(props: CurrencyInputProps) {
  const [text, setText] = useState(props.value?.toString() ?? "");
  // A value set from outside (a reset, a prefill) wins over stale text.
  const displayValue =
    parseAmount(text) === props.value ? text : (props.value?.toString() ?? "");

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const pasted =
      (e.nativeEvent as InputEvent).inputType === "insertFromPaste";
    const input = normalizeAmountInput(e.target.value, pasted);
    if (input === undefined) {
      return;
    }
    setText(input);
    props.onChange(parseAmount(input));
  }

  return (
    <ShowFormErrors show={props.forceValidation}>
      <FormItem
        label={`${props.label} (${props.currency})`}
        error={props.errorMessage}
      >
        <TextInput
          value={displayValue}
          onChange={handleChange}
          inputMode="decimal"
          placeholder={props.placeholder}
          width={claimsFieldWidth.token}
          data-testid={props.dataTestId}
        />
      </FormItem>
    </ShowFormErrors>
  );
}
